// help-widget.js — floating Help button + slide-out panel with three tabs:
//   1. Ask (new ticket — auto-fills current module/view/last-result context)
//   2. My Tickets (status badges, threaded view)
//   3. Knowledge Base (markdown docs)
//
// Wires to /api/tickets backend. RBAC handled server-side.

const HELP = {
  open: false,
  tab: 'ask',
  myTickets: [],
  activeTicket: null,
  lastContext: {},
  rendered: false,
};

function token() { return localStorage.getItem('gdbs_token') || ''; }
function authed() { return !!token(); }

async function api(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, {
    method, headers, body: body ? JSON.stringify(body) : undefined
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`${method} ${path}: ${res.status} ${txt.slice(0, 200)}`);
  }
  return res.json();
}

function currentModule() {
  const active = document.querySelector('.view.active');
  if (!active) return 'general';
  const id = active.id || '';
  return id.replace(/^view-/, '') || 'general';
}

async function currentTier() {
  try {
    const { getLicense, getUser } = await import('./auth.js');
    const lic = getLicense();
    const user = getUser();
    // Admin role outranks tier — surface as "admin" so support staff see who's pinging.
    if (user?.role === 'admin') return 'admin';
    // Prefer license.tier; fall back to user.tier (older license payloads), then trial.
    return lic?.tier || lic?.plan || user?.tier || 'trial';
  } catch { return 'trial'; }
}

// Public API for other modules to push context (e.g. after a failed computation)
window.gdbsCaptureHelpContext = function(ctx) {
  HELP.lastContext = { ...HELP.lastContext, ...ctx, capturedAt: new Date().toISOString() };
};

async function autoCaptureContext() {
  const tier = await currentTier();
  const ctx = {
    module: currentModule(),
    tier,
    url: window.location.pathname + window.location.hash,
    userAgent: navigator.userAgent.slice(0, 200),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString(),
    ...HELP.lastContext,
  };
  return ctx;
}

function ensureWidget() {
  if (document.getElementById('gdbs-help-fab')) return;

  const style = document.createElement('style');
  style.id = 'gdbs-help-style';
  // Theme-aware: every chrome color resolves to a CSS var defined in
  // app.css (:root, dark default) and flipped by theme-bridge.css for
  // [data-theme="light"]. No hardcoded slate/navy hex codes.
  style.textContent = `
    #gdbs-help-fab {
      position: fixed; bottom: 24px; right: 24px; z-index: 9998;
      width: 56px; height: 56px; border-radius: 50%;
      background: linear-gradient(135deg,
        var(--accent),
        color-mix(in oklch, var(--accent) 70%, oklch(20% 0.04 260)));
      color: var(--text-bright, #fff); border: none; cursor: pointer;
      box-shadow: 0 4px 14px color-mix(in oklch, var(--accent) 35%, transparent);
      display: flex; align-items: center; justify-content: center;
      font-size: 22px; transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    #gdbs-help-fab:hover {
      transform: scale(1.08);
      box-shadow: 0 6px 18px color-mix(in oklch, var(--accent) 50%, transparent);
    }
    #gdbs-help-panel {
      position: fixed; bottom: 88px; right: 24px; z-index: 9999;
      width: 440px; max-width: calc(100vw - 48px); height: 600px; max-height: calc(100vh - 120px);
      background: var(--bg-card, var(--bg-panel));
      border: 1px solid var(--border);
      border-radius: 10px;
      box-shadow: 0 8px 32px color-mix(in oklch, var(--text) 22%, transparent);
      color: var(--text); font-size: 13px; display: flex; flex-direction: column;
      overflow: hidden;
    }
    #gdbs-help-panel.hidden { display: none; }
    .gdbs-help-header {
      padding: 12px 16px;
      background: var(--bg-alt);
      border-bottom: 1px solid var(--border);
      display: flex; justify-content: space-between; align-items: center;
    }
    .gdbs-help-header h3 { margin: 0; font-size: 15px; color: var(--text); }
    .gdbs-help-close { background: none; border: none; color: var(--text-dim); cursor: pointer; font-size: 18px; }
    .gdbs-help-close:hover { color: var(--text); }
    .gdbs-help-tabs { display: flex; border-bottom: 1px solid var(--border); background: var(--bg-alt); }
    .gdbs-help-tab {
      flex: 1; padding: 10px; background: none; color: var(--text-dim);
      border: none; border-bottom: 2px solid transparent;
      cursor: pointer; font-size: 12px; font-family: inherit;
      transition: color 120ms ease, border-bottom-color 120ms ease, background 120ms ease;
    }
    .gdbs-help-tab:hover { color: var(--text); background: color-mix(in oklch, var(--accent) 7%, transparent); }
    .gdbs-help-tab.active {
      color: var(--accent);
      border-bottom-color: var(--accent);
      background: color-mix(in oklch, var(--accent) 9%, transparent);
    }
    .gdbs-help-body { flex: 1; overflow-y: auto; padding: 14px 16px; }
    .gdbs-help-body h4 { color: var(--text); margin: 0 0 8px 0; font-size: 13px; }
    .gdbs-help-body label {
      display: block; margin: 8px 0 4px; color: var(--text-dim); font-size: 11px;
      font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    }
    .gdbs-help-body input, .gdbs-help-body select, .gdbs-help-body textarea {
      width: 100%; padding: 7px 9px;
      background: var(--bg, var(--bg-input));
      border: 1px solid var(--border);
      border-radius: 4px;
      color: var(--text); font-size: 12px; box-sizing: border-box;
      font-family: inherit;
    }
    .gdbs-help-body input:focus, .gdbs-help-body select:focus, .gdbs-help-body textarea:focus {
      outline: 1px solid var(--accent); outline-offset: -1px;
    }
    .gdbs-help-body textarea { min-height: 100px; resize: vertical; }
    .gdbs-help-body button.primary {
      width: 100%; padding: 9px;
      background: var(--accent); color: var(--text-bright, #fff);
      border: none; border-radius: 4px; cursor: pointer; font-weight: 600;
      margin-top: 12px; font-size: 12px;
      transition: filter 120ms ease;
    }
    .gdbs-help-body button.primary:hover { filter: brightness(1.1); }
    .gdbs-help-body button.primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .gdbs-ticket-card {
      padding: 10px 12px;
      background: var(--bg-alt);
      border: 1px solid var(--border);
      border-radius: 6px; margin-bottom: 8px; cursor: pointer;
      transition: border-color 120ms ease, background 120ms ease;
    }
    .gdbs-ticket-card:hover {
      border-color: var(--accent);
      background: color-mix(in oklch, var(--accent) 6%, var(--bg-alt));
    }
    .gdbs-ticket-row { display: flex; gap: 8px; align-items: center; font-size: 11px; color: var(--text-dim); }
    .gdbs-ticket-subject { color: var(--text); font-weight: 600; font-size: 12px; margin: 2px 0; }
    .gdbs-badge {
      display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 10px;
      font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    }
    /* Status / priority badges — semantic hue, theme-mixed surface */
    .gdbs-badge-open        { background: color-mix(in oklch, oklch(60% 0.16 240) 18%, transparent); color: oklch(60% 0.16 240); }
    .gdbs-badge-in_progress { background: color-mix(in oklch, oklch(70% 0.16 80) 18%, transparent);  color: oklch(55% 0.16 70); }
    .gdbs-badge-waiting     { background: color-mix(in oklch, oklch(65% 0.18 290) 18%, transparent); color: oklch(55% 0.18 290); }
    .gdbs-badge-resolved    { background: color-mix(in oklch, oklch(65% 0.16 150) 18%, transparent); color: oklch(45% 0.16 150); }
    .gdbs-badge-closed      { background: color-mix(in oklch, var(--text-dim) 20%, transparent);     color: var(--text-dim); }
    .gdbs-badge-p1          { background: color-mix(in oklch, oklch(60% 0.20 25) 22%, transparent);  color: oklch(55% 0.20 25); }
    .gdbs-badge-p2          { background: color-mix(in oklch, oklch(70% 0.16 80) 18%, transparent);  color: oklch(55% 0.16 70); }
    .gdbs-msg { padding: 8px 10px; border-radius: 4px; margin-bottom: 6px; font-size: 12px; }
    .gdbs-msg-user  { background: var(--bg-alt); }
    .gdbs-msg-admin { background: color-mix(in oklch, var(--accent) 13%, transparent); }
    .gdbs-msg-meta  { font-size: 10px; color: var(--text-dim); margin-bottom: 3px; }
    .gdbs-help-ctx {
      background: var(--bg, var(--bg-input));
      border: 1px solid var(--border);
      padding: 6px 10px; border-radius: 4px;
      font-size: 10px; color: var(--text-dim); margin: 6px 0;
      font-family: var(--mono, monospace);
    }
    .gdbs-empty { text-align: center; color: var(--text-dim); padding: 30px 10px; font-size: 12px; }
  `;
  document.head.appendChild(style);

  // FAB is intentionally NOT created — Help & Support now lives in the user
  // dropdown menu (top-right of the topbar). The floating button was overlapping
  // chart download buttons and Report controls. The panel itself is unchanged
  // and still toggled by window.gdbsHelpToggle() from the menu item.

  const panel = document.createElement('div');
  panel.id = 'gdbs-help-panel';
  panel.classList.add('hidden');
  panel.innerHTML = `
    <div class="gdbs-help-header">
      <h3>Help &amp; Support</h3>
      <button class="gdbs-help-close" onclick="window.gdbsHelpToggle()">&times;</button>
    </div>
    <div class="gdbs-help-tabs">
      <button class="gdbs-help-tab active" data-tab="ask">Ask</button>
      <button class="gdbs-help-tab" data-tab="mine">My Tickets</button>
      <button class="gdbs-help-tab" data-tab="kb">Knowledge Base</button>
    </div>
    <div class="gdbs-help-body" id="gdbs-help-body"></div>
  `;
  document.body.appendChild(panel);

  panel.querySelectorAll('.gdbs-help-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      panel.querySelectorAll('.gdbs-help-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      HELP.tab = btn.dataset.tab;
      renderBody();
    });
  });
}

function togglePanel() {
  ensureWidget();
  HELP.open = !HELP.open;
  document.getElementById('gdbs-help-panel').classList.toggle('hidden', !HELP.open);
  if (HELP.open) renderBody();
}
window.gdbsHelpToggle = togglePanel;

function renderBody() {
  const body = document.getElementById('gdbs-help-body');
  if (!body) return;
  if (!authed()) {
    body.innerHTML = `<div class="gdbs-empty">Please sign in to open or view support tickets.</div>`;
    return;
  }
  if (HELP.tab === 'ask') return renderAsk(body);
  if (HELP.tab === 'mine') return renderMine(body);
  if (HELP.tab === 'kb') return renderKB(body);
}

const TICKET_TEMPLATES = [
  { id: 'bug',     label: 'Bug report',
    category: 'bug',      priority: 'P2',
    subject_template: 'Bug: [short description]',
    body_template: 'What you did:\n\nWhat you expected:\n\nWhat happened instead:\n\nSteps to reproduce:\n1. \n2. \n3. \n' },
  { id: 'physics', label: 'Physics question',
    category: 'physics',  priority: 'P3',
    subject_template: 'Physics: [topic]',
    body_template: 'Module/tab: [where you are]\n\nWhat physics behavior you observed:\n\nWhy you think this might be wrong:\n\nReference you compared against (DOI if available):\n' },
  { id: 'feature', label: 'Feature request',
    category: 'feature',  priority: 'P4',
    subject_template: 'Feature: [what to add]',
    body_template: 'What you want to do:\n\nWhy the current tools fall short:\n\nWhat similar tools (or papers) do this well:\n' },
  { id: 'billing', label: 'Billing / account',
    category: 'billing',  priority: 'P3',
    subject_template: 'Billing: [issue]',
    body_template: 'Account email: [yours]\n\nWhat you expected to be able to do:\n\nWhat happened:\n' },
];

async function renderAsk(body) {
  const ctx = await autoCaptureContext();
  body.innerHTML = `
    <h4>New Support Ticket</h4>
    <p style="color:var(--text-dim,#94a3b8);font-size:11px;line-height:1.5;">
      We auto-capture the module you're in and any recent computation context. Premium customers get priority queue.
    </p>
    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;">
      ${TICKET_TEMPLATES.map(t => `
        <button class="hp-template" data-template="${t.id}"
          style="background:var(--bg-alt,#0f1f33);border:1px solid var(--border,#1e3a5f);color:var(--text,#cbd5e1);padding:4px 10px;border-radius:14px;cursor:pointer;font-size:11px;">
          ${t.label}
        </button>
      `).join('')}
    </div>
    <label>Module</label>
    <select id="hp-module">
      <option value="${ctx.module}">${ctx.module} (current)</option>
      <option value="plasma">plasma</option>
      <option value="quantum">quantum</option>
      <option value="fluids">fluids</option>
      <option value="materials">materials</option>
      <option value="geophysics">geophysics</option>
      <option value="ballistics">ballistics</option>
      <option value="theory">theory</option>
      <option value="general">general / other</option>
      <option value="billing">billing / account</option>
    </select>
    <label>Category</label>
    <select id="hp-category">
      <option value="question">question</option>
      <option value="bug">bug</option>
      <option value="physics">physics interpretation</option>
      <option value="feature">feature request</option>
      <option value="billing">billing</option>
    </select>
    <label>Priority</label>
    <select id="hp-priority">
      <option value="P3">P3 - normal</option>
      <option value="P4">P4 - low</option>
      <option value="P2">P2 - high</option>
      <option value="P1">P1 - blocking</option>
    </select>
    <label>Subject</label>
    <input id="hp-subject" maxlength="200" placeholder="Short description">
    <label>Details</label>
    <textarea id="hp-body" maxlength="8000" placeholder="What were you trying to do? What did you see? What did you expect?"></textarea>
    <div class="gdbs-help-ctx">Auto-context: module=${ctx.module}, tier=${ctx.tier}, ${ctx.viewport}</div>
    <button class="primary" id="hp-submit">Open ticket</button>
    <div id="hp-status" style="margin-top:10px;color:var(--text-dim,#94a3b8);font-size:11px;"></div>
  `;
  // Wire ticket templates — clicking pre-fills category/priority/subject/body
  document.querySelectorAll('.hp-template').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = TICKET_TEMPLATES.find(x => x.id === btn.dataset.template);
      if (!t) return;
      document.getElementById('hp-category').value = t.category;
      document.getElementById('hp-priority').value = t.priority;
      const subjectEl = document.getElementById('hp-subject');
      const bodyEl = document.getElementById('hp-body');
      if (!subjectEl.value) subjectEl.value = t.subject_template;
      if (!bodyEl.value)    bodyEl.value    = t.body_template;
    });
  });

  // Apply any pre-fill stashed by gdbsOpenTicketWithContext
  if (HELP._prefill) {
    const p = HELP._prefill;
    if (p.module && document.getElementById('hp-module'))    document.getElementById('hp-module').value = p.module;
    if (p.category && document.getElementById('hp-category'))document.getElementById('hp-category').value = p.category;
    if (p.priority && document.getElementById('hp-priority'))document.getElementById('hp-priority').value = p.priority;
    if (p.subject)   document.getElementById('hp-subject').value = p.subject;
    if (p.body)      document.getElementById('hp-body').value = p.body;
    HELP._prefill = null;
  }

  document.getElementById('hp-submit').onclick = async () => {
    const btn = document.getElementById('hp-submit');
    const subject = document.getElementById('hp-subject').value.trim();
    const text = document.getElementById('hp-body').value.trim();
    if (!subject || !text) {
      document.getElementById('hp-status').textContent = 'Subject and details are required.';
      return;
    }
    btn.disabled = true;
    document.getElementById('hp-status').textContent = 'Submitting...';
    try {
      const req = {
        subject,
        body: text,
        module: document.getElementById('hp-module').value,
        category: document.getElementById('hp-category').value,
        priority: document.getElementById('hp-priority').value,
        context: ctx,
      };
      const res = await api('POST', '/api/tickets', req);
      document.getElementById('hp-status').innerHTML =
        `Ticket opened. Reference: <code>${res.ticket.id.slice(0,8)}</code>`;
      setTimeout(() => { HELP.tab = 'mine'; renderBody();
        document.querySelectorAll('.gdbs-help-tab').forEach(b => b.classList.toggle('active', b.dataset.tab==='mine'));
      }, 1200);
    } catch (e) {
      document.getElementById('hp-status').textContent = 'Failed: ' + e.message;
      btn.disabled = false;
    }
  };
}

async function renderMine(body) {
  body.innerHTML = `<div class="gdbs-empty">Loading...</div>`;
  try {
    const res = await api('GET', '/api/tickets/mine');
    HELP.myTickets = res.tickets || [];
    if (HELP.activeTicket) {
      const fresh = HELP.myTickets.find(t => t.id === HELP.activeTicket.id);
      if (fresh) HELP.activeTicket = fresh;
    }
    if (HELP.activeTicket) {
      renderTicketDetail(body, HELP.activeTicket);
    } else {
      renderTicketList(body, HELP.myTickets);
    }
  } catch (e) {
    body.innerHTML = `<div class="gdbs-empty">Could not load tickets: ${e.message}</div>`;
  }
}

function renderTicketList(body, list) {
  if (list.length === 0) {
    body.innerHTML = `<div class="gdbs-empty">No tickets yet. Use the Ask tab to open one.</div>`;
    return;
  }
  body.innerHTML = `<h4>Your Tickets</h4>` + list.map(t => `
    <div class="gdbs-ticket-card" onclick="window.gdbsHelpOpenTicket('${t.id}')">
      <div class="gdbs-ticket-row">
        <span class="gdbs-badge gdbs-badge-${t.status}">${t.status.replace('_',' ')}</span>
        ${t.priority === 'P1' || t.priority === 'P2'
          ? `<span class="gdbs-badge gdbs-badge-${t.priority.toLowerCase()}">${t.priority}</span>` : ''}
        <span>${t.module}</span> · <span>${t.category}</span>
      </div>
      <div class="gdbs-ticket-subject">${escapeHtml(t.subject)}</div>
      <div class="gdbs-ticket-row">${new Date(t.updatedAt).toLocaleString()} · ${t.messages?.length || 0} replies</div>
    </div>
  `).join('');
}

window.gdbsHelpOpenTicket = function(id) {
  const t = HELP.myTickets.find(x => x.id === id);
  if (!t) return;
  HELP.activeTicket = t;
  renderBody();
};

function renderTicketDetail(body, t) {
  body.innerHTML = `
    <button class="gdbs-help-close" style="font-size:14px;color:var(--accent,#4fc3f7);float:left;cursor:pointer;background:none;border:none;padding:0;margin-bottom:8px;"
      onclick="window.gdbsHelpBackToList()">&larr; Back</button>
    <div style="clear:both"></div>
    <h4>${escapeHtml(t.subject)}</h4>
    <div class="gdbs-ticket-row" style="margin-bottom:10px;">
      <span class="gdbs-badge gdbs-badge-${t.status}">${t.status.replace('_',' ')}</span>
      <span>${t.module}</span> · <span>${t.category}</span> · <span>${t.priority}</span>
    </div>
    <div class="gdbs-msg gdbs-msg-user">
      <div class="gdbs-msg-meta">You · ${new Date(t.createdAt).toLocaleString()}</div>
      ${escapeHtml(t.body)}
    </div>
    ${(t.messages || []).map(m => `
      <div class="gdbs-msg gdbs-msg-${m.authorRole}">
        <div class="gdbs-msg-meta">${m.authorRole === 'admin' ? 'Support' : 'You'} · ${new Date(m.createdAt).toLocaleString()}</div>
        ${escapeHtml(m.body)}
      </div>
    `).join('')}
    ${t.status !== 'closed' ? `
      <label style="margin-top:14px;">Reply</label>
      <textarea id="hp-reply" maxlength="8000"></textarea>
      <button class="primary" id="hp-send">Send reply</button>
    ` : `<div class="gdbs-empty">This ticket is closed.</div>`}
    <div id="hp-reply-status" style="color:var(--text-dim,#94a3b8);font-size:11px;margin-top:6px;"></div>
  `;
  const sendBtn = document.getElementById('hp-send');
  if (sendBtn) sendBtn.onclick = async () => {
    const text = document.getElementById('hp-reply').value.trim();
    if (!text) return;
    sendBtn.disabled = true;
    try {
      await api('POST', `/api/tickets/${t.id}/reply`, { body: text });
      document.getElementById('hp-reply-status').textContent = 'Sent.';
      await renderMine(document.getElementById('gdbs-help-body'));
    } catch (e) {
      document.getElementById('hp-reply-status').textContent = 'Send failed: ' + e.message;
      sendBtn.disabled = false;
    }
  };
}

window.gdbsHelpBackToList = function() {
  HELP.activeTicket = null;
  renderBody();
};

// In-memory KB index + body cache. Lazy-fetched on first KB open.
const KB = {
  index: null,
  bodies: {},            // id -> markdown text
  searchCache: null,     // [{id, title, section, body}] for filtering
};

async function loadKBIndex() {
  if (KB.index) return KB.index;
  try {
    const res = await fetch('/api/docs/guide', { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('gdbs_token') || '') }, cache: 'no-store' });
    if (!res.ok) throw new Error('not 200');
    KB.index = await res.json();
  } catch (_) { KB.index = { topics: [] }; }
  return KB.index;
}

async function loadKBBody(id) {
  if (KB.bodies[id]) return KB.bodies[id];
  try {
    const res = await fetch(`/api/docs/guide/${encodeURIComponent(id)}`, { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('gdbs_token') || '') }, cache: 'no-store' });
    KB.bodies[id] = res.ok ? await res.text() : '';
  } catch (_) { KB.bodies[id] = ''; }
  return KB.bodies[id];
}

async function buildSearchIndex() {
  if (KB.searchCache) return KB.searchCache;
  const idx = await loadKBIndex();
  KB.searchCache = await Promise.all(
    (idx.topics || []).map(async t => ({
      id: t.id,
      title: t.title,
      section: t.section,
      body: await loadKBBody(t.id),
    }))
  );
  return KB.searchCache;
}

function filterKB(entries, q) {
  if (!q) return entries;
  const ql = q.toLowerCase();
  return entries.filter(e =>
    (e.title || '').toLowerCase().includes(ql) ||
    (e.section || '').toLowerCase().includes(ql) ||
    (e.body || '').toLowerCase().includes(ql)
  );
}

async function renderKB(body) {
  body.innerHTML = `
    <h4>Knowledge Base</h4>
    <input id="hp-kb-search" placeholder="Search all docs and glossary..."
      style="width:100%;padding:7px 9px;margin-bottom:10px;">
    <div id="hp-kb-list" style="display:flex;flex-direction:column;gap:6px;"></div>
  `;
  const listEl = document.getElementById('hp-kb-list');
  const searchEl = document.getElementById('hp-kb-search');

  const renderList = (filtered) => {
    if (filtered.length === 0) {
      listEl.innerHTML = '<div class="gdbs-empty">No matches.</div>';
      return;
    }
    // Group by section, preserving section order from the index
    const sections = {};
    const sectionOrder = [];
    for (const e of filtered) {
      if (!sections[e.section]) { sections[e.section] = []; sectionOrder.push(e.section); }
      sections[e.section].push(e);
    }
    listEl.innerHTML = sectionOrder.map(sect => `
      <div style="margin-top:8px;color:var(--text-dim,#64748b);font-size:10px;text-transform:uppercase;letter-spacing:0.06em;font-weight:700;">${escapeHtml(sect || 'General')}</div>
      ${sections[sect].map(e => `
        <a class="gdbs-ticket-card" href="javascript:void(0)" data-doc-id="${e.id}">
          <div class="gdbs-ticket-subject">${escapeHtml(e.title)}</div>
          <div class="gdbs-ticket-row">${kbSnippet(e.body, searchEl.value)}</div>
        </a>
      `).join('')}
    `).join('');
    listEl.querySelectorAll('[data-doc-id]').forEach(a => {
      a.addEventListener('click', () => {
        const id = a.dataset.docId;
        togglePanel();   // close help drawer
        window.gdbsOpenDocs?.(id);
      });
    });
  };

  listEl.innerHTML = '<div class="gdbs-empty">Loading...</div>';
  const entries = await buildSearchIndex();
  // Also surface the Physics Glossary as a pseudo-entry
  const allEntries = [
    ...entries,
    { id: 'glossary', title: 'Physics Glossary', section: 'Reference',
      body: 'physics symbols definitions citations β α κ δ ι s rho Troyon Greenwald Soloviev ballooning Mercier' },
  ];
  renderList(allEntries);
  let lastQ = '';
  searchEl.addEventListener('input', () => {
    const q = searchEl.value.trim();
    if (q === lastQ) return;
    lastQ = q;
    renderList(filterKB(allEntries, q));
  });
}

function escapeHtml(s) {
  if (!s) return '';
  return s.replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function kbSnippet(body, q) {
  if (!body) return '';
  const plain = body.replace(/[#*`>]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!q || !plain.toLowerCase().includes(q.toLowerCase())) {
    return escapeHtml(plain.slice(0, 100)) + (plain.length > 100 ? '…' : '');
  }
  // Show a window around the match for context
  const i = plain.toLowerCase().indexOf(q.toLowerCase());
  const start = Math.max(0, i - 30);
  const end = Math.min(plain.length, i + q.length + 70);
  const before = start > 0 ? '…' : '';
  const after = end < plain.length ? '…' : '';
  return before + escapeHtml(plain.slice(start, end)) + after;
}

// Public API: programmatically open the help drawer with a ticket pre-filled.
// Used from validation FAIL → "Report" link, docs → "Open ticket about this", etc.
window.gdbsOpenTicketWithContext = function(prefill) {
  ensureWidget();
  HELP.tab = 'ask';
  // Stash the prefill into lastContext so renderAsk sees it via autoCaptureContext.
  if (prefill) {
    HELP.lastContext = { ...HELP.lastContext, ...(prefill.context || {}) };
    HELP._prefill = prefill;
  }
  HELP.open = true;
  document.getElementById('gdbs-help-panel').classList.remove('hidden');
  document.querySelectorAll('.gdbs-help-tab').forEach(b =>
    b.classList.toggle('active', b.dataset.tab === 'ask'));
  renderBody();
  // After renderAsk inflates the form, populate fields with prefill
  setTimeout(() => {
    if (!prefill) return;
    if (prefill.module)   document.getElementById('hp-module')?.value && (document.getElementById('hp-module').value = prefill.module);
    if (prefill.category) document.getElementById('hp-category') && (document.getElementById('hp-category').value = prefill.category);
    if (prefill.priority) document.getElementById('hp-priority') && (document.getElementById('hp-priority').value = prefill.priority);
    if (prefill.subject)  document.getElementById('hp-subject') && (document.getElementById('hp-subject').value = prefill.subject);
    if (prefill.body)     document.getElementById('hp-body') && (document.getElementById('hp-body').value = prefill.body);
  }, 50);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureWidget);
} else {
  ensureWidget();
}

export { togglePanel, autoCaptureContext };
